/**
 * V2.0 Phase 4b — /opportunities page.
 *
 * Sections rendered top → bottom:
 *  1. Compliance reminder banner
 *  2. Your Biggest Opportunity ⭐
 *  3. 🏦 Business Funding Opportunities
 *  4. 🏠 Mortgage Opportunities
 *  5. 💳 Personal Credit Opportunities
 *  6. 📈 Progress Forecast
 *  7. 📅 Action Timeline
 *  8. 💰 Estimated Financial Impact
 *  9. Bottom Actions
 *
 * All data comes from ONE call to /api/opportunities/summary.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Briefcase, CreditCard, DollarSign,
  ExternalLink, Home, Info, ListChecks, Percent, Target, TrendingUp,
} from "lucide-react";
import { api, formatApiError } from "@/api/client";
import AppNav from "@/components/app/AppNav";


const APPROVAL_TONE = {
  Excellent: { bg: "rgba(52,211,153,0.10)", fg: "#0F9F70", border: "rgba(52,211,153,0.32)" },
  Strong: { bg: "rgba(94,234,212,0.10)", fg: "#5EEAD4", border: "rgba(94,234,212,0.32)" },
  Moderate: { bg: "rgba(251,191,36,0.10)", fg: "#FBBF24", border: "rgba(251,191,36,0.32)" },
};

const MORTGAGE_TIER_TONE = {
  mortgage_ready: { fg: "#0F9F70", bg: "rgba(52,211,153,0.10)", border: "rgba(52,211,153,0.32)" },
  nearly_ready: { fg: "#FBBF24", bg: "rgba(251,191,36,0.10)", border: "rgba(251,191,36,0.32)" },
  needs_improvement: { fg: "#FB923C", bg: "rgba(251,146,60,0.10)", border: "rgba(251,146,60,0.32)" },
  build_foundation: { fg: "#F87171", bg: "rgba(248,113,113,0.10)", border: "rgba(248,113,113,0.32)" },
};

const FUNDING_TYPE_LABEL = {
  term_loan: "Term Loan",
  line_of_credit: "Line of Credit",
  equipment_financing: "Equipment Financing",
  sba: "SBA",
  merchant_cash_advance: "Merchant Cash Advance",
  invoice_factoring: "Invoice Factoring",
  other: "Business Funding",
};

const CATEGORY_LABEL = {
  credit_builder: "Credit Builder",
  secured: "Secured",
  balance_transfer: "Balance Transfer",
  zero_apr: "0% Intro APR",
  rewards_starter: "Starter Rewards",
};

const ICON_MAP = {
  briefcase: Briefcase,
  home: Home,
  "credit-card": CreditCard,
  "trending-up": TrendingUp,
  percent: Percent,
  "dollar-sign": DollarSign,
};

const fmtUsd = (n) => (n == null ? "—" : `$${Number(n).toLocaleString()}`);
const fmtPct = (n) => (n == null ? "—" : `${Number(n).toFixed(1)}%`);


export default function Opportunities() {
  const [data, setData] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: payload }, { data: dash }] = await Promise.all([
        api.get("/opportunities/summary"),
        api.get("/dashboard/summary").catch(() => ({ data: null })),
      ]);
      setData(payload);
      setDashboard(dash);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (loading && !data) {
    return (
      <div className="min-h-screen" style={{ background: "var(--v2-background)", color: "var(--v2-text)" }}>
        <AppNav />
        <div className="max-w-6xl mx-auto px-6 py-16 text-sm text-[var(--v2-text-muted)]">Loading your opportunities…</div>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div data-testid="opportunities-page" className="min-h-screen" style={{ background: "var(--v2-background)", color: "var(--v2-text)" }}>
      <AppNav />
      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10 space-y-12">
        <Link
          to="/dashboard"
          data-testid="opportunities-back-link"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[var(--v2-text-muted)] hover:text-[var(--v2-text)]"
        >
          <ArrowLeft className="w-3 h-3" /> Back to dashboard
        </Link>

        <header data-testid="opportunities-page-header" className="-mt-6">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[var(--v2-accent)]">
            <Target className="w-3.5 h-3.5" /> Funding Opportunities
          </div>
          <h1
            data-testid="opportunities-page-h1"
            className="font-semibold tracking-tight text-[30px] md:text-[36px] leading-tight text-[var(--v2-text)] mt-2"
          >
            What you can qualify for today
          </h1>
          <p
            data-testid="opportunities-page-subtitle"
            className="mt-2 text-sm text-[var(--v2-text-muted)] leading-relaxed max-w-2xl"
          >
            See what business funding, mortgage options, and personal credit products you qualify for today.
          </p>
        </header>

        <ComplianceBanner text={data.compliance_banner} />
        <BiggestOpportunity data={data.biggest_opportunity} />
        <BusinessFunding section={data.business_funding} />
        <MortgageSection section={data.mortgage} complianceLine={data.mortgage_compliance_line} />
        <PersonalCredit section={data.personal_credit} />
        <ProgressForecast forecast={data.progress_forecast} />
        <ActionTimeline timeline={data.action_timeline} />
        <ImpactSummary tiles={data.estimated_financial_impact} />
        <BottomActions nextBestAction={dashboard?.next_best_action} />
      </main>
    </div>
  );
}


/**
 * V2.0 Phase 4b Tweak 1 — small route lookup used as a fallback when the
 * dashboard summary payload omits the `route` sibling on next_best_action.
 * The backend already surfaces `{code, label, route}` today; this map is a
 * belt-and-braces guard for older cached payloads.
 */
const NEXT_BEST_ACTION_ROUTE_FALLBACK = {
  complete_profile: "/profile",
  upload_report: "/credit-report",
  review_analysis: "/credit-report",
  generate_first_letter: "/dispute-letters/new",
  improve_credit: "/roadmap",
  view_partners: "/partners",
  review_mortgage: "/mortgage-intake",
};

export function resolveNextBestActionRoute(nba) {
  if (!nba) return { route: "/roadmap", label: "Continue Your Financial Blueprint" };
  const route = nba.route || NEXT_BEST_ACTION_ROUTE_FALLBACK[nba.code] || "/roadmap";
  const label = nba.label || "Continue Your Financial Blueprint";
  return { route, label };
}


/* ---------- Section 1 — Compliance banner ------------------------------ */
function ComplianceBanner({ text }) {
  return (
    <div
      data-testid="opportunities-compliance-banner"
      className="rounded-2xl px-4 py-3 flex items-start gap-3"
      style={{ background: "var(--v2-surface-subtle)", border: "1px solid var(--v2-border)" }}
    >
      <div
        className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ background: "rgba(167,139,250,0.14)", color: "var(--v2-accent)" }}
      >
        <Info className="w-3.5 h-3.5" />
      </div>
      <div className="text-xs text-[var(--v2-text-muted)] leading-relaxed">{text}</div>
    </div>
  );
}


/* ---------- Section 2 — Your Biggest Opportunity ⭐ --------------------- */
function BiggestOpportunity({ data }) {
  if (!data) return null;
  const Icon = ICON_MAP[data.icon] || Target;
  return (
    <section data-testid="opportunities-biggest" id="biggest">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--v2-accent)] flex items-center gap-1.5 mb-3">
        <Target className="w-3.5 h-3.5" /> Your Biggest Opportunity
      </div>
      <div
        className="rounded-2xl p-6 md:p-8 relative overflow-hidden"
        style={{
          background:
            "var(--ds-color-accent-soft)",
          border: "1px solid rgba(167,139,250,0.28)",
        }}
      >
        <div className="grid md:grid-cols-[64px_1fr_auto] gap-5 items-start">
          <div
            className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(167,139,250,0.20)", color: "var(--v2-accent)" }}
          >
            <Icon className="w-7 h-7" />
          </div>
          <div className="space-y-3">
            <h2 data-testid="opportunities-biggest-headline" className="font-display text-[24px] md:text-[30px] leading-tight tracking-tight">
              {data.headline}
            </h2>
            <p className="text-sm text-[var(--v2-text-muted)] leading-relaxed max-w-2xl">{data.narrative}</p>
            <div
              data-testid="opportunities-biggest-value"
              className="inline-flex items-center rounded-full px-3 py-1.5 text-sm font-medium"
              style={{ background: "var(--v2-surface-subtle)", color: "var(--v2-text)", border: "1px solid var(--v2-border)" }}
            >
              {data.value_display}
            </div>
          </div>
          <Link
            to={data.cta_route || "/dashboard"}
            data-testid="opportunities-biggest-cta"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-medium flex-shrink-0"
            style={{ background: "var(--v2-accent)", color: "var(--v2-accent-ink)" }}
          >
            {data.cta_label} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="mt-5 pt-4 border-t border-[var(--v2-border)] text-[11px] text-[var(--v2-text-muted)]">
          {data.compliance_line}
        </div>
      </div>
    </section>
  );
}


/* ---------- Section 3 — 🏦 Business Funding ---------------------------- */
function BusinessFunding({ section }) {
  return (
    <section data-testid="opportunities-business-funding" id="business-funding">
      <SectionHeader
        icon={Briefcase}
        eyebrow="Business Funding"
        title="Funding partners you already qualify for"
        subtitle="Only lenders whose eligibility criteria match your current profile appear here."
      />
      {section.state === "not_business_owner" && (
        <EmptyState
          testId="opportunities-bf-empty-not-owner"
          text="Business funding opportunities appear here once your profile confirms you're a business owner."
        />
      )}
      {section.state === "empty" && (
        <EmptyState
          testId="opportunities-bf-empty"
          text="Business funding opportunities appear here once you complete your business profile."
        />
      )}
      {section.state === "eligible" && (
        <>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {section.top_partners.map((p) => (
              <PartnerCard key={p.partner_id} partner={p} />
            ))}
          </div>
          <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
            <Link
              to={section.view_all_route}
              data-testid="opportunities-bf-view-all"
              className="text-xs uppercase tracking-[0.18em] text-[var(--v2-accent)] hover:opacity-80 inline-flex items-center gap-1.5"
            >
              View All {section.total_eligible_count} Qualified Funding Partners <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </>
      )}
      <ComplianceMicro text={section.compliance_line} />
    </section>
  );
}

function PartnerCard({ partner }) {
  const tone = APPROVAL_TONE[partner.approval_strength] || APPROVAL_TONE.Moderate;
  const nextStep = partner.estimated_next_step;
  return (
    <div
      data-testid={`opportunities-partner-${partner.partner_id}`}
      className="rounded-2xl p-5 space-y-3.5"
      style={{ background: "var(--v2-surface)", border: "1px solid var(--v2-border)" }}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--v2-text-muted)]">
            {FUNDING_TYPE_LABEL[partner.funding_type] || partner.funding_type}
          </div>
          <div className="font-display text-lg text-[var(--v2-text)] mt-0.5">{partner.partner_name}</div>
        </div>
        <div
          className="rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] flex-shrink-0"
          style={{ background: tone.bg, color: tone.fg, border: `1px solid ${tone.border}` }}
        >
          {partner.approval_strength}
        </div>
      </div>
      {partner.funding_range_label && (
        <div className="text-sm text-[var(--v2-text-muted)]">
          Estimated range: <span className="text-[var(--v2-text)]">{partner.funding_range_label}</span>
        </div>
      )}
      {partner.why_qualify?.length > 0 && (
        <ul className="text-xs space-y-1.5 text-[var(--v2-text-muted)]">
          {partner.why_qualify.slice(0, 3).map((w, i) => (
            <li key={i} className="pl-3.5 relative">
              <span className="absolute left-0 top-1.5 w-1 h-1 rounded-full bg-[var(--v2-accent)]"></span>
              {w}
            </li>
          ))}
        </ul>
      )}
      <div className="pt-2 border-t border-[var(--v2-border)] flex items-center justify-between gap-2 text-xs">
        <span className="text-[var(--v2-text-muted)]">Next step:</span>
        <span className="text-[var(--v2-text)] font-medium">{nextStep}</span>
      </div>
      {partner.application_link && (
        <a
          href={partner.application_link}
          target="_blank"
          rel="noreferrer"
          data-testid={`opportunities-partner-cta-${partner.partner_id}`}
          className="inline-flex items-center gap-1 text-xs text-[var(--v2-accent)] hover:opacity-80"
        >
          Open partner site <ExternalLink className="w-3 h-3" />
        </a>
      )}
    </div>
  );
}


/* ---------- Section 4 — 🏠 Mortgage ------------------------------------ */
function MortgageSection({ section, complianceLine }) {
  const state = section?.state;
  return (
    <section data-testid="opportunities-mortgage" id="mortgage">
      <SectionHeader
        icon={Home}
        eyebrow="Mortgage Readiness"
        title="Your path to homeownership"
        subtitle="Estimated purchase power, eligible loan programs and the improvements that unlock more."
      />
      {state === "intake_incomplete" && (
        <div
          data-testid="opportunities-mortgage-empty"
          className="rounded-2xl p-6 flex flex-wrap items-center gap-4 justify-between"
          style={{ background: "var(--v2-surface)", border: "1px dashed var(--v2-border)" }}
        >
          <div className="text-sm text-[var(--v2-text-muted)] max-w-md">
            Complete your Mortgage Readiness intake to see your purchase power, monthly payment estimate and eligible loan programs.
          </div>
          <Link
            to="/mortgage-intake"
            data-testid="opportunities-mortgage-cta"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-medium"
            style={{ background: "var(--v2-accent)", color: "var(--v2-accent-ink)" }}
          >
            Start Mortgage Intake <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      )}
      {state === "intake_complete" && <MortgageDetails payload={section.payload} complianceLine={complianceLine} />}
    </section>
  );
}

function MortgageDetails({ payload, complianceLine }) {
  const tier = payload.mortgage_readiness_tier || {};
  const tone = MORTGAGE_TIER_TONE[tier.code] || MORTGAGE_TIER_TONE.build_foundation;
  const pp = payload.purchase_power || {};
  const breakdown = pp.payment_breakdown || {};
  return (
    <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,320px)] gap-5">
      <div className="rounded-2xl p-6 space-y-5" style={{ background: "var(--v2-surface)", border: "1px solid var(--v2-border)" }}>
        <div className="flex items-baseline gap-3">
          <div data-testid="opportunities-mortgage-score" className="font-display text-[48px]" style={{ color: tone.fg }}>
            {payload.mortgage_readiness_score}
          </div>
          <div className="text-xs text-[var(--v2-text-muted)]">/ 100</div>
          <div
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] uppercase tracking-[0.18em] ml-auto"
            style={{ background: tone.bg, color: tone.fg, border: `1px solid ${tone.border}` }}
          >
            {tier.emoji} {tier.label}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--v2-text-muted)] mb-1">Estimated purchase power</div>
          <div data-testid="opportunities-mortgage-purchase-power" className="font-display text-[30px]">
            {fmtUsd(pp.estimated_purchase_power_max)}
          </div>
        </div>
        {payload.recommended_programs?.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--v2-text-muted)] mb-2">Recommended programs</div>
            <ul className="grid sm:grid-cols-2 gap-2">
              {payload.recommended_programs.map((p) => (
                <li
                  key={p.code}
                  data-testid={`opportunities-mortgage-program-${p.code}`}
                  className="rounded-lg p-3 text-xs"
                  style={{ background: "var(--v2-surface-subtle)", border: "1px solid var(--v2-border)" }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[var(--v2-text)] text-sm">{p.label}</span>
                    <span
                      className="rounded-full px-1.5 py-0.5 text-[10px] uppercase"
                      style={{
                        background: p.eligibility === "eligible" ? "rgba(52,211,153,0.10)" : "rgba(251,191,36,0.10)",
                        color: p.eligibility === "eligible" ? "#0F9F70" : "#FBBF24",
                      }}
                    >
                      {p.eligibility === "eligible" ? "Eligible" : "Nearly eligible"}
                    </span>
                  </div>
                  <p className="text-[var(--v2-text-muted)] mt-1">{p.note}</p>
                </li>
              ))}
            </ul>
          </div>
        )}
        {payload.top_improvements?.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--v2-text-muted)] mb-2">Top improvements</div>
            <ul className="space-y-1.5">
              {payload.top_improvements.slice(0, 3).map((imp, i) => (
                <li
                  key={`${imp.bucket}-${i}`}
                  className="flex items-center gap-3 text-xs text-[var(--v2-text-muted)]"
                >
                  <span
                    className="rounded-full px-1.5 py-0.5 text-[10px] font-medium"
                    style={{ background: "rgba(167,139,250,0.10)", color: "var(--v2-accent)", border: "1px solid rgba(167,139,250,0.30)" }}
                  >
                    +{imp.impact_pts} pts
                  </span>
                  <span>{imp.label}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <div className="space-y-4">
        <div className="rounded-2xl p-5" style={{ background: "var(--v2-surface)", border: "1px solid var(--v2-border)" }}>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--v2-text-muted)] mb-3">Monthly payment</div>
          <div className="font-display text-[24px]">{fmtUsd(pp.estimated_monthly_payment)}</div>
          <div className="mt-3 space-y-1.5 text-xs">
            <PmtRow label="Principal & interest" value={breakdown.principal_interest} />
            <PmtRow label="Property tax" value={breakdown.property_tax} />
            <PmtRow label="Insurance" value={breakdown.homeowners_insurance} />
            <PmtRow label="PMI" value={breakdown.pmi} />
            <PmtRow label="HOA" value={breakdown.hoa} />
            <div className="border-t border-[var(--v2-border)] pt-1.5 mt-1.5 flex items-center justify-between">
              <span className="text-[var(--v2-text-muted)]">Down payment (assumed)</span>
              <span className="text-[var(--v2-text)]">{fmtUsd(pp.assumed_down_payment)}</span>
            </div>
          </div>
        </div>
        <ComplianceMicro text={complianceLine} testId="opportunities-mortgage-compliance" />
      </div>
    </div>
  );
}

function PmtRow({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[var(--v2-text-muted)]">{label}</span>
      <span className="text-[var(--v2-text)]">{fmtUsd(value)}</span>
    </div>
  );
}


/* ---------- Section 5 — 💳 Personal Credit ----------------------------- */
function PersonalCredit({ section }) {
  const [showNearly, setShowNearly] = useState(false);
  return (
    <section data-testid="opportunities-personal-credit" id="personal-credit">
      <SectionHeader
        icon={CreditCard}
        eyebrow="Personal Credit"
        title="Products that match your file today"
        subtitle="Educational entries — sample offers admin will replace with real affiliate matches over time."
      />
      {section.eligible.length === 0 ? (
        <EmptyState
          testId="opportunities-pc-empty"
          text="Complete your profile so we can match you to eligible personal-credit products."
        />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {section.eligible.slice(0, 6).map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}
      {section.nearly_eligible?.length > 0 && (
        <div className="mt-6">
          <button
            type="button"
            data-testid="opportunities-pc-toggle-nearly"
            onClick={() => setShowNearly((v) => !v)}
            className="text-xs uppercase tracking-[0.18em] text-[var(--v2-text-muted)] hover:text-[var(--v2-text)] inline-flex items-center gap-1.5"
          >
            {showNearly ? "Hide" : "Show"} nearly-eligible ({section.nearly_eligible.length})
            <ArrowRight className={`w-3 h-3 transition-transform ${showNearly ? "rotate-90" : ""}`} />
          </button>
          {showNearly && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {section.nearly_eligible.map((p) => (
                <ProductCard key={p.id} product={p} nearly />
              ))}
            </div>
          )}
        </div>
      )}
      <ComplianceMicro text={section.compliance_note} testId="opportunities-pc-compliance" />
    </section>
  );
}

function ProductCard({ product, nearly = false }) {
  const cat = CATEGORY_LABEL[product.category] || product.category;
  const aprLow = product.typical_apr_low_pct;
  const aprHigh = product.typical_apr_high_pct;
  return (
    <div
      data-testid={`opportunities-product-${product.id}`}
      className={`rounded-2xl p-5 space-y-3 ${nearly ? "opacity-95" : ""}`}
      style={{ background: "var(--v2-surface)", border: `1px solid var(--v2-border)` }}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-display text-base text-[var(--v2-text)] leading-tight">{product.name}</div>
          <div className="text-[11px] text-[var(--v2-text-muted)]">{product.provider}</div>
        </div>
        <span
          className="rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] flex-shrink-0"
          style={{ background: "rgba(167,139,250,0.10)", color: "var(--v2-accent)", border: "1px solid rgba(167,139,250,0.30)" }}
        >
          {cat}
        </span>
      </div>
      <p className="text-xs text-[var(--v2-text-muted)]">{product.primary_benefit}</p>
      <div className="grid grid-cols-2 gap-2 text-[11px] text-[var(--v2-text-muted)] border-t border-[var(--v2-border)] pt-2">
        <div>APR: <span className="text-[var(--v2-text)]">{aprLow != null && aprHigh != null ? `${aprLow}%–${aprHigh}%` : "Varies"}</span></div>
        <div>Fee: <span className="text-[var(--v2-text)]">{product.annual_fee_usd > 0 ? `$${product.annual_fee_usd}` : "None"}</span></div>
      </div>
      {nearly && product.what_you_need && (
        <div className="text-[11px] rounded-md px-2 py-1.5" style={{ background: "rgba(251,191,36,0.08)", color: "#FBBF24" }}>
          {product.what_you_need}
        </div>
      )}
      {product.application_link && (
        <a
          href={product.application_link}
          target="_blank"
          rel="noreferrer"
          data-testid={`opportunities-product-cta-${product.id}`}
          className="inline-flex items-center gap-1 text-xs text-[var(--v2-accent)] hover:opacity-80"
        >
          Learn more <ExternalLink className="w-3 h-3" />
        </a>
      )}
    </div>
  );
}


/* ---------- Section 6 — 📈 Progress Forecast --------------------------- */
function ProgressForecast({ forecast }) {
  if (!forecast) return null;
  const rows = [
    { key: "funding_readiness", label: "Funding Readiness", cur: forecast.funding_readiness?.current, proj: forecast.funding_readiness?.projected, suffix: "/100" },
    { key: "mortgage_readiness", label: "Mortgage Readiness", cur: forecast.mortgage_readiness?.current, proj: forecast.mortgage_readiness?.projected, suffix: "/100" },
    { key: "funding_partners", label: "Funding Partners Eligible", cur: forecast.funding_partners?.current, proj: forecast.funding_partners?.projected, suffix: "" },
    { key: "purchase_power", label: "Estimated Purchase Power", cur: forecast.purchase_power?.current, proj: forecast.purchase_power?.projected, kind: "usd" },
    { key: "interest_rate", label: "Estimated Interest Rate", cur: forecast.interest_rate?.current_pct, proj: forecast.interest_rate?.projected_pct, kind: "pct" },
    { key: "monthly_payment_savings", label: "Estimated Monthly Payment Savings", cur: null, proj: forecast.monthly_payment_savings?.monthly_delta, kind: "delta_usd" },
  ];
  return (
    <section data-testid="opportunities-forecast" id="forecast">
      <SectionHeader
        icon={TrendingUp}
        eyebrow="Progress Forecast"
        title="If you complete your top 3 recommendations…"
        subtitle="Educational projection of where your file could land."
      />
      <div className="rounded-2xl overflow-hidden" style={{ background: "var(--v2-surface)", border: "1px solid var(--v2-border)" }}>
        <div className="grid md:grid-cols-3 text-[10px] uppercase tracking-[0.16em] text-[var(--v2-text-muted)] py-2 px-4 border-b border-[var(--v2-border)]" style={{ background: "var(--v2-surface-subtle)" }}>
          <div>Metric</div>
          <div>Current</div>
          <div>Projected</div>
        </div>
        {rows.map((r) => (
          <ForecastRow key={r.key} row={r} />
        ))}
      </div>
      <ComplianceMicro text={forecast.compliance_line} testId="opportunities-forecast-compliance" />
    </section>
  );
}

function ForecastRow({ row }) {
  const format = (v) => {
    if (v == null) return "—";
    if (row.kind === "usd" || row.kind === "delta_usd") return fmtUsd(v);
    if (row.kind === "pct") return fmtPct(v);
    return `${v}${row.suffix || ""}`;
  };
  const positive = row.proj != null && row.cur != null && Number(row.proj) > Number(row.cur);
  return (
    <div
      data-testid={`opportunities-forecast-row-${row.key}`}
      className="grid md:grid-cols-3 items-center px-4 py-3 border-b border-[var(--v2-border)] last:border-b-0"
    >
      <div className="text-sm text-[var(--v2-text)] mb-1 md:mb-0">{row.label}</div>
      <div className="text-sm text-[var(--v2-text-muted)] tabular-nums">{format(row.cur)}</div>
      <div className={`text-sm tabular-nums ${positive ? "text-[#0F9F70]" : "text-[var(--v2-text)]"}`}>
        {row.kind === "delta_usd" && row.proj != null ? `+${format(row.proj)}/mo` : format(row.proj)}
      </div>
    </div>
  );
}


/* ---------- Section 7 — 📅 Action Timeline ----------------------------- */
function ActionTimeline({ timeline }) {
  if (!timeline) return null;
  const columns = [
    { key: "today", label: "Today", items: timeline.today },
    { key: "this_week", label: "This Week", items: timeline.this_week },
    { key: "next_30_days", label: "Next 30 Days", items: timeline.next_30_days },
    { key: "next_90_days", label: "Next 90 Days", items: timeline.next_90_days },
  ];
  return (
    <section data-testid="opportunities-timeline" id="timeline">
      <SectionHeader
        icon={ListChecks}
        eyebrow="Action Timeline"
        title="What to do — when"
        subtitle="A prioritized sequence based on your current state."
      />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {columns.map((col) => (
          <div
            key={col.key}
            data-testid={`opportunities-timeline-${col.key}`}
            className="rounded-2xl p-5 space-y-3"
            style={{ background: "var(--v2-surface)", border: "1px solid var(--v2-border)" }}
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--v2-accent)]">{col.label}</div>
            {col.items.length === 0 ? (
              <div className="text-xs text-[var(--v2-text-muted)] italic">Nothing prioritized.</div>
            ) : (
              <ul className="space-y-2">
                {col.items.map((it, i) => (
                  <li key={`${col.key}-${i}`} className="text-sm text-[var(--v2-text-muted)] flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--v2-accent)] mt-2 flex-shrink-0" />
                    {it.cta_route ? (
                      <Link
                        to={it.cta_route}
                        data-testid={`opportunities-timeline-item-${col.key}-${i}`}
                        className="hover:text-[var(--v2-text)]"
                      >
                        {it.label}
                      </Link>
                    ) : (
                      <span data-testid={`opportunities-timeline-item-${col.key}-${i}`}>{it.label}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}


/* ---------- Section 8 — 💰 Estimated Financial Impact ------------------ */
function ImpactSummary({ tiles }) {
  if (!tiles || tiles.length === 0) return null;
  return (
    <section data-testid="opportunities-impact" id="impact">
      <SectionHeader
        icon={DollarSign}
        eyebrow="Estimated Financial Impact"
        title="What all this could unlock"
      />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {tiles.map((t, i) => {
          const Icon = ICON_MAP[t.icon] || TrendingUp;
          return (
            <div
              key={i}
              data-testid={`opportunities-impact-${i}`}
              className="rounded-2xl p-5 flex items-start gap-3"
              style={{ background: "var(--v2-surface)", border: "1px solid var(--v2-border)" }}
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: "rgba(167,139,250,0.14)", color: "var(--v2-accent)" }}
              >
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <div className="text-sm text-[var(--v2-text)] font-medium">{t.headline}</div>
                <div className="text-xs text-[var(--v2-text-muted)] mt-1">{t.subline}</div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}


/* ---------- Section 9 — Bottom actions --------------------------------- */
function BottomActions({ nextBestAction }) {
  const { route: primaryRoute, label: primaryLabel } = resolveNextBestActionRoute(nextBestAction);
  return (
    <section data-testid="opportunities-bottom-actions" className="pt-2 flex flex-wrap items-center gap-3 border-t border-[var(--v2-border)]">
      <Link
        to={primaryRoute}
        data-testid="opportunities-continue-blueprint"
        data-nba-code={nextBestAction?.code || ""}
        className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium"
        style={{ background: "var(--v2-accent)", color: "var(--v2-accent-ink)" }}
      >
        {primaryLabel} <ArrowRight className="w-3.5 h-3.5" />
      </Link>
      <Link
        to="/roadmap"
        data-testid="opportunities-view-roadmap"
        className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium border border-[var(--v2-border)] text-[var(--v2-text-muted)] hover:text-[var(--v2-text)]"
      >
        View Roadmap
      </Link>
      <Link
        to="/dashboard"
        data-testid="opportunities-return-dashboard"
        className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium border border-[var(--v2-border)] text-[var(--v2-text-muted)] hover:text-[var(--v2-text)]"
      >
        Return to Dashboard
      </Link>
    </section>
  );
}


/* ---------- Small helpers --------------------------------------------- */
function SectionHeader({ icon: Icon, eyebrow, title, subtitle }) {
  return (
    <header className="mb-4">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--v2-accent)] inline-flex items-center gap-1.5 mb-1">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {eyebrow}
      </div>
      <h2 className="font-display text-[24px] md:text-[30px] leading-tight tracking-tight">{title}</h2>
      {subtitle && (
        <p className="text-sm text-[var(--v2-text-muted)] mt-1 max-w-2xl">{subtitle}</p>
      )}
    </header>
  );
}

function ComplianceMicro({ text, testId }) {
  if (!text) return null;
  return (
    <div
      data-testid={testId || "opportunities-compliance-micro"}
      className="text-[11px] text-[var(--v2-text-muted)] leading-relaxed mt-4"
    >
      {text}
    </div>
  );
}

function EmptyState({ text, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl p-6 text-sm text-[var(--v2-text-muted)] italic"
      style={{ background: "var(--v2-surface)", border: "1px dashed var(--v2-border)" }}
    >
      {text}
    </div>
  );
}
